import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import { DialogCloseSaveButtons } from 'components/Common/DialogActionButtons/DialogCloseSaveButtons'
import BomItemBatchUpdateGrid from 'features/BillOfMaterials/BatchEditRowsDialog/BomItemBatchUpdateGrid'
import { isEqual } from 'lodash'
import { AssemblyHeaderRow, PartTypeRow } from 'model/Project/BoMItemRow'
import { useEffect, useState } from 'react'
import { useAppSelector } from 'store/configureStore'
import projectImageUrl from 'services/APIs/InternalAPI/ProjectImages'
import { RootAppState, useAppDispatch } from 'store/configureStore'
import { BoMItemSatisfyActiveFilters } from 'utils/BoMItemSatisfyActiveFilters'
import { useOrganizationAndProjectContext } from 'utils/useOrganizationContext'
import { ReactComponent as FilterIconSvg } from '_icons/project-menu/filter.svg'
import FiltersContainer from '../BoMItemFilter/Components/FiltersContainer'
import { batchUpdateBomItems } from '../store/asyncActions/batchUpdateBomItems'
import { newProjectActions } from '../store/newProjectReducer'
import { projectOperationSelector } from '../store/selectors/projectOperationPendingSelector'
import { selectedBomItemsSelector } from '../store/selectors/selectedBomItemsSelector'
import { BomItemUpdateGridModel } from './BomItemUpdateGridModel'

export type BatchEditBomItemsDialogProps = {
  open: boolean
  onClose: () => void
}

const filterSelector = (state: RootAppState) => state.project.filters

const assembliesSelector = (state: RootAppState): AssemblyHeaderRow[] => {
  return state.project.assemblyHeadersIds.reduce((acc, curr) => {
    if (
      BoMItemSatisfyActiveFilters(
        filterSelector(state),
        state.project.flatView[curr]
      )
    ) {
      const assemblyHeader = state.project.flatView[curr] as AssemblyHeaderRow

      if (
        assemblyHeader?.filteredPartTypeIds?.length > 0 &&
        assemblyHeader?.assembly?.partTypeIds?.length > 0
      ) {
        acc.push(assemblyHeader)
      }
    }

    return acc
  }, [])
}

export const BatchEditRows = ({
  open,
  onClose,
}: BatchEditBomItemsDialogProps) => {
  const [rows, setRows] = useState<BomItemUpdateGridModel[]>([])
  const { partyId, projectId } = useOrganizationAndProjectContext()
  const dispatch = useAppDispatch()

  const selectedBomItems = useAppSelector(
    selectedBomItemsSelector,
    isEqual
  ) as PartTypeRow[]

  const assemblies = useAppSelector(assembliesSelector, isEqual)

  const operationStatus = useAppSelector(
    projectOperationSelector(batchUpdateBomItems.typePrefix),
    isEqual
  )

  useEffect(() => {
    if (operationStatus?.requestStatus === 'fulfilled') {
      onClose()
    }
  }, [onClose, operationStatus?.requestStatus])

  useEffect(() => {
    setRows(
      selectedBomItems.map((x) => ({
        id: x.id,
        rowNumber: x.rowNumber,
        thumbnail3d: projectImageUrl(partyId, projectId, x.thumbnail3d),
        name: x.name,
        quantity: x.financial?.quantity,
        keywords: x.materialSummary.tokens,
        comment: x.comment,
        enableChangeQuantity: x.canChangeQuantity,
        originalFileName: x.originalFileName,
      }))
    )
  }, [partyId, projectId, selectedBomItems])

  const handleSave = () => {
    dispatch(
      batchUpdateBomItems(
        rows.map((x) => ({
          bomItemId: x.id.toString(),
          quantity: x.quantity,
          keywords: x.keywords,
          comment: x.comment,
        }))
      )
    )
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        Assemblies
        <Autocomplete
          onChange={(event, value) => {
            if (typeof value === 'string') {
              return
            }
            const selectedRows = value
              .map((x) => {
                if (typeof x !== 'string') {
                  return x.filteredPartTypeIds
                }
                return null
              })
              .flat()

            if (selectedRows) {
              dispatch(
                newProjectActions.setSelectedBomItems(
                  selectedRows.filter((x) => x !== null)
                )
              )
            }
          }}
          multiple
          options={assemblies}
          renderInput={(params) => <TextField {...params} />}
          getOptionLabel={(option) => option.assembly.name}
          renderOption={(option) => (
            <div style={{ display: 'flex', gap: '1em', alignItems: 'center' }}>
              {option.assembly.thumbnail3d ? (
                <img
                  loading="lazy"
                  src={projectImageUrl(
                    partyId,
                    projectId,
                    option.assembly.thumbnail3d
                  )}
                  style={{
                    width: '3rem',
                  }}
                />
              ) : (
                <div style={{ width: '3rem' }} />
              )}
              <Typography variant="body1">{option.assembly.name}</Typography>
            </div>
          )}
        />
        <Accordion>
          <AccordionSummary>
            <FilterIconSvg style={{ width: '3em' }} />
          </AccordionSummary>
          <AccordionDetails>
            <FiltersContainer />
          </AccordionDetails>
        </Accordion>
      </DialogTitle>
      <DialogContent>
        <BomItemBatchUpdateGrid
          bomItemUpdateModels={rows}
          setBomItemUpdateModel={setRows}
        />
      </DialogContent>
      <DialogActions>
        <DialogCloseSaveButtons
          onCloseButtonClicked={onClose}
          onSaveButtonClicked={handleSave}
          saving={operationStatus?.requestStatus === 'pending'}
        />
      </DialogActions>
    </Dialog>
  )
}
